import { AloftPrice } from '@aloft/toolkit-shared';
import { Block, Flex, ProductColor, SimpleInput, Typography } from '@aloft/toolkit-sui';

import { useMemo } from 'react';

import { setOpinionOfSiteValue } from '../../../redux/features/actions/setOpinionOfSiteValue.ts';
import { useAppraisifyValidation } from '../../../hooks/validation/useAppraisifyValidation.ts';
import { selectOpinionOfSiteValuePrice } from '../../../redux/features/selectors/opinionOfSiteValueSelectors.ts';
import {
  selectCompType,
  selectIsArvOrder,
} from '../../../redux/features/selectors/orderSelectors.ts';
import { selectImprovementsCost, selectImprovementsCostDisplay } from '../../../redux/features/selectors/selectImprovementsCost.ts';
import { selectPropertyValueDisplayById } from '../../../redux/features/selectors/selectPropertyValue.ts';
import { selectSiteValue, selectSiteValueDisplay } from '../../../redux/features/selectors/selectSiteValue.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { AivArvActiveTab } from '../../../redux/types.ts';
import { schPrice } from '../../analysis/types.ts';

const formatPercent = (n: number | undefined) => {
  if (n === undefined || Number.isNaN(n)) return undefined;
  return `(${n}%)`;
}

const computePercents = (improvements: AloftPrice | undefined, site: AloftPrice | undefined) => {
  const total = improvements && site && improvements.add(site).toDollarLike();
  if (!total) return { improvementsPct: undefined, sitePct: undefined };
  const improvementsPct = Math.round((improvements.toDollarLike() / total) * 100);
  const sitePct = 100 - improvementsPct;
  return { improvementsPct, sitePct };
}

const Row = ({
  label,
  right,
  isTotal = false,
  alignItems = 'center',
}: {
  label: React.ReactNode;
  right: React.ReactNode;
  isTotal?: boolean;
  alignItems?: 'center' | 'flex-start';
}) => (
  <Flex
    justifyContent="space-between"
    alignItems={alignItems}
    gap="0.5rem"
    __dangerDoNotUseSx={
      isTotal ? { padding: '0.35rem 0.75rem', fontWeight: 700 } : { padding: '0.35rem 0.75rem' }
    }
  >
    {typeof label === 'string' ? (
      <Typography
        variant={isTotal ? 'h4' : 'body1'}
        __dangerDoNotUseSx={isTotal ? undefined : { fontWeight: 400 }}
      >
        {label}
      </Typography>
    ) : (
      label
    )}
    <Flex
      justifyContent="flex-end"
      alignItems={alignItems}
      gap="0.5rem"
    >
      {right}
    </Flex>
  </Flex>
);

export const useIndicatedCostByID = (aivArvTab?: AivArvActiveTab) =>
  useMemo(
    () =>
      ({ id }: { id: string }) => {
        const dispatch = useAppDispatch();
        const isEditableSubject = id === 'subject';
        const isArvOrder = useAppSelector(selectIsArvOrder);
        const compType = useAppSelector((state) => selectCompType(state, id));

        const totalValue = useAppSelector((state) => selectPropertyValueDisplayById(state, id));
        const improvementsValue = useAppSelector((state) =>
          selectImprovementsCostDisplay(state, id),
        );
        const siteValueDisplay = useAppSelector((state) => selectSiteValueDisplay(state, id));

        const improvements = useAppSelector((state) => selectImprovementsCost(state, id));
        const site = useAppSelector((state) => selectSiteValue(state, id));
        const { improvementsPct, sitePct } = computePercents(improvements, site);

        const opinionOfSiteValue = useAppSelector(selectOpinionOfSiteValuePrice);
        const { errors } = useAppraisifyValidation();
        const siteValueError = errors?.Analysis?.opinionOfSiteValue;

        const onSubjectSiteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
          const value = schPrice.safeParse(event.target.value);
          const parsedValue = value.success
            ? (value.data ?? AloftPrice.zero.toJSON())
            : AloftPrice.zero.toJSON();
          dispatch(setOpinionOfSiteValue(parsedValue));
        };

        let backgroundColor: ProductColor;
        if (isArvOrder) {
          if (id === 'subject') {
            backgroundColor = 'aivLight';
          } else if (id === 'subjectArv') {
            backgroundColor = 'arvLight';
          } else if (compType === 'aiv') {
            backgroundColor = 'aivLight';
          } else if (compType === 'arv') {
            backgroundColor = 'arvLight';
          } else {
            backgroundColor = 'surface1';
          }
        } else {
          backgroundColor = 'alertInfoLight';
        }

        let totalLabel: string;
        if (isArvOrder && (id === 'subject' || id === 'subjectArv')) {
          totalLabel = id === 'subject' ? 'As-Is' : 'After Repair';
        } else {
          totalLabel = 'Indicated Cost';
        }

        let tag:
          | { text: string; backgroundColor: ProductColor; textColor: ProductColor }
          | undefined;
        if (isArvOrder && compType) {
          tag = {
            text: compType.toUpperCase(),
            backgroundColor: (compType === 'aiv' ? 'aiv' : 'arv') as ProductColor,
            textColor: (compType === 'aiv' ? 'aivText' : 'arvText') as ProductColor,
          };
        } else {
          tag = undefined;
        }

        const totalLabelNode =
          tag && id !== 'subject' && id !== 'subjectArv' ? (
            <Flex alignItems="center" gap="0.5rem">
              <Typography variant="h4">{totalLabel}</Typography>
              <Flex
                backgroundColor={tag.backgroundColor}
                __dangerDoNotUseSx={{
                  padding: '4px 8px',
                  borderRadius: '6px',
                }}
              >
                <Typography
                  variant="body2"
                  color={tag.textColor}
                  __dangerDoNotUseSx={{ lineHeight: 1 }}
                >
                  {tag.text}
                </Typography>
              </Flex>
            </Flex>
          ) : (
            totalLabel
          );

        return (
          <Flex flexDirection="column" backgroundColor={backgroundColor} height="100%" gap={0}>
            <Row
              label={totalLabelNode}
              isTotal
              right={<Typography variant="h4">{totalValue ?? '$ -'}</Typography>}
            />
            <Row
              label="Improvements"
              right={
                <>
                  <Typography variant="body1" __dangerDoNotUseSx={{ fontWeight: 400 }}>
                    {improvementsValue ?? '$ -'}
                  </Typography>
                  {formatPercent(improvementsPct) && (
                    <Typography
                      variant="body1"
                      color="textSecondary"
                      __dangerDoNotUseSx={{ fontWeight: 400 }}
                    >
                      {formatPercent(improvementsPct)}
                    </Typography>
                  )}
                </>
              }
            />
            <Row
              label="Site"
              alignItems={siteValueError ? 'flex-start' : 'center'}
              right={
                isEditableSubject ? (
                  <>
                    <Block width="8rem" __dangerDoNotUseSx={{ flexShrink: 0, minWidth: 0 }}>
                      <Block>
                        <SimpleInput
                          id={`indicated-cost-site-${id}`}
                          type="number"
                          precision={0}
                          size="sm"
                          prefix="$"
                          useSeparator
                          width="100%"
                          required
                          value={opinionOfSiteValue?.toString(true, true)}
                          onChange={onSubjectSiteChange}
                          invalid={!!siteValueError}
                        />
                        {/* Always render error space to prevent layout shift */}
                        <Block minHeight="1.25rem" paddingTop="0.25rem">
                          {siteValueError?.message && (
                            <Typography variant="body2" color="alertError">
                              {siteValueError.message}
                            </Typography>
                          )}
                        </Block>
                      </Block>
                    </Block>
                    {formatPercent(sitePct) && (
                      <Typography
                        variant="body1"
                        color="textSecondary"
                        __dangerDoNotUseSx={{ fontWeight: 400 }}
                      >
                        {formatPercent(sitePct)}
                      </Typography>
                    )}
                  </>
                ) : (
                  <>
                    <Typography variant="body1" __dangerDoNotUseSx={{ fontWeight: 400 }}>
                      {siteValueDisplay ?? '$ -'}
                    </Typography>
                    {formatPercent(sitePct) && (
                      <Typography
                        variant="body1"
                        color="textSecondary"
                        __dangerDoNotUseSx={{ fontWeight: 400 }}
                      >
                        {formatPercent(sitePct)}
                      </Typography>
                    )}
                  </>
                )
              }
            />
          </Flex>
        );
      },
    [aivArvTab],
  );
